import { useQuery } from '@tanstack/react-query';
import { questRepository, QuestDescription, QuestStatusResult } from '@/repositories/quest-repository';
import { useAuth } from '@/hooks';
import { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CAMPAIGN_CFG } from '@/configs/campaign-config';

const QUESTS_ENABLED = CAMPAIGN_CFG.features.quests.enabled !== false;
const QUESTS_DISABLED_ERROR_MESSAGE = 'Quests are unavailable during the current campaign.';
const QUEST_GAME_TYPES = ['general', 'coinflip', 'dice', 'limbo', 'lootbox', 'plinko'] as const;
const DEFAULT_QUEST_POINTS = 100;
const DEFAULT_QUEST_GOAL = 1;

const claimQuestDisabled = async () => ({
	success: false as const,
	error: QUESTS_DISABLED_ERROR_MESSAGE,
});

const ALL_QUESTS_DISABLED = {
	questsByGame: {} as Record<string, GameQuest[]>,
	totalClaimedXp: 0,
	isLoading: false,
	error: null,
	claimQuest: claimQuestDisabled,
	refreshQuests: () => {},
} as const;

const makeGameQuestsDisabled = () => {
	const disabledMutation = {
		mutateAsync: async (_variables: { questKey: string }) => {
			throw new Error(QUESTS_DISABLED_ERROR_MESSAGE);
		},
	};

	return {
		quests: [] as GameQuest[],
		isLoading: false,
		error: null,
		changedQuests: new Set<string>(),
		claimQuest: disabledMutation,
		refreshQuests: () => {},
	} as const;
};

export interface GameQuest {
	key: string;
	title: string;
	description: string;
	isCompleted: boolean;
	completedAt?: Date;
	points: number;
	progress: number;
	goal: number;
	claimStatus?: 'in_progress' | 'ready_to_claim' | 'claimed' | 'external' | 'upcoming' | 'expired';
	claimedAt?: Date;
	timePeriod?: 'ALL_TIME' | 'DAILY' | 'WEEKLY' | 'MONTHLY';
	gameType?: string; // Add gameType to help with navigation
	claimable?: boolean;
	managedExternally?: boolean;
	state?: 'inactive' | 'upcoming' | 'active' | 'expired';
	statusLoaded?: boolean;
	period?: {
		type: 'ALL_TIME' | 'DAILY' | 'WEEKLY' | 'MONTHLY';
		periodStart: string;
		periodEnd: string | null;
		effectiveStart: string;
		effectiveEnd: string | null;
	};
}

const formatQuestTitle = (questKey: string) =>
	questKey
		.replace(/_/g, ' ')
		.replace(/\b\w/g, (letter) => letter.toUpperCase());

const deriveQuestGameType = (questKey: string) => {
	const prefix = questKey.split('_')[0] ?? 'general';
	return prefix === 'general' ? 'general' : prefix;
};

const normalizeQuestStatus = (status?: QuestStatusResult) => {
	const managedExternally = Boolean(status?.managedExternally);
	const claimStatus = (status?.claimStatus || (managedExternally ? 'external' : 'in_progress')) as GameQuest['claimStatus'];
	return { managedExternally, claimStatus };
};

const buildGameQuest = (
	quest: QuestDescription & { gameType?: string },
	status?: QuestStatusResult,
	options?: { statusLoaded?: boolean; claimableFallback?: boolean },
): GameQuest => {
	const { managedExternally, claimStatus } = normalizeQuestStatus(status);
	const statusLoaded = options?.statusLoaded ?? Boolean(status);
	const claimableFallback = options?.claimableFallback ?? (!managedExternally && claimStatus !== 'claimed');
	const timePeriod = status?.timePeriod ?? status?.period?.type ?? quest.timePeriod;

	return {
		key: quest.key,
		title: quest.title || formatQuestTitle(quest.key),
		description: quest.description ?? '',
		points: status?.points ?? quest.points ?? DEFAULT_QUEST_POINTS,
		goal: status?.goal ?? quest.goal ?? DEFAULT_QUEST_GOAL,
		timePeriod,
		isCompleted: status?.isValid ?? false,
		completedAt: status?.validatedAt ? new Date(status.validatedAt) : undefined,
		claimedAt: status?.claimedAt ? new Date(status.claimedAt) : undefined,
		progress: status?.progress ?? 0,
		claimStatus,
		claimable: status?.claimable ?? claimableFallback,
		managedExternally,
		state: status?.state as GameQuest['state'],
		period: status?.period,
		gameType: quest.gameType ?? deriveQuestGameType(quest.key),
		statusLoaded,
	};
};

// Hook for quest page that supports time period filtering
export const useAllQuests = (timePeriodFilter?: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'ALL_TIME') => {
	const { currentAccount } = useAuth();

	const queryClient = useQueryClient();

	// Fetch all quests from all games
	const {
		data: allQuests,
		isLoading: questsLoading,
		error: questsError,
	} = useQuery({
		queryKey: ['allQuests', timePeriodFilter],
		queryFn: async () => {
			const quests = await questRepository.getAllQuests();
			return quests.map((quest) => ({ ...quest, gameType: deriveQuestGameType(quest.key) }));
		},
		enabled: QUESTS_ENABLED,
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

	// Filter quests by time period
	const filteredQuests = useMemo(() => {
		if (!allQuests) return [];

		if (!timePeriodFilter || timePeriodFilter === 'ALL_TIME') {
			return allQuests;
		}

		return allQuests.filter((quest) => quest.timePeriod === timePeriodFilter);
	}, [allQuests, timePeriodFilter]);

	// Group quests by game type
	const questsByGame = useMemo(() => {
		if (!filteredQuests) return {};

		const grouped: Record<string, QuestDescription[]> = {};

		QUEST_GAME_TYPES.forEach((gameType) => {
			if (gameType === 'general') {
				grouped[gameType] = filteredQuests.filter((quest) => quest.key.startsWith('general_'));
			} else {
				grouped[gameType] = filteredQuests.filter((quest) => quest.key.startsWith(`${gameType}_`));
			}
		});

		return grouped;
	}, [filteredQuests]);

	// Fetch quest statuses for all quests - fetch immediately without waiting for quest definitions
	const {
		data: questStatuses,
		isLoading: statusesLoading,
		error: statusesError,
	} = useQuery({
		queryKey: ['allQuestStatuses', currentAccount?.address, timePeriodFilter],
		queryFn: async () => {
			if (!currentAccount) return {};

			// Fetch quest definitions inline if not available yet, or use existing ones
			let questsToCheck = filteredQuests;

			if (!questsToCheck || questsToCheck.length === 0) {
				// Fetch quest definitions inline to avoid waiting
				const quests = await questRepository.getAllQuests();
				const allQuestsData = quests.map((quest) => ({ ...quest, gameType: deriveQuestGameType(quest.key) }));

				// Apply time period filter
				if (!timePeriodFilter || timePeriodFilter === 'ALL_TIME') {
					questsToCheck = allQuestsData;
				} else {
					questsToCheck = allQuestsData.filter((quest) => quest.timePeriod === timePeriodFilter);
				}
			}

			if (!questsToCheck || questsToCheck.length === 0) {
				return {};
			}

			const questsPayload = questsToCheck.map((quest) => ({
				key: quest.key,
				mode: quest.timePeriod,
			}));

			const statuses = await questRepository.checkQuestStatuses('suigar', currentAccount.address, questsPayload);
			return statuses;
		},
		enabled: QUESTS_ENABLED && !!currentAccount,
		staleTime: 30 * 1000, // 30 seconds
		refetchInterval: 60 * 1000, // Refetch every minute
	});

	// Combine quest definitions with user statuses. If user isn't logged in,
	// still return quest definitions with default (neutral) progress.
	const questsWithStatus = useMemo(() => {
		const result: Record<string, GameQuest[]> = {};

		// If quest definitions are available, prefer them
		if (questsByGame && Object.keys(questsByGame).length > 0) {
			Object.entries(questsByGame).forEach(([gameType, quests]) => {
				// Ensure no duplicate quests in each category
				const uniqueQuests = quests.filter((quest, index, array) => array.findIndex((q) => q.key === quest.key) === index);

				result[gameType] = uniqueQuests.map((quest): GameQuest => {
					const userStatus = questStatuses?.[quest.key] as QuestStatusResult | undefined;
					const statusLoaded = currentAccount ? Boolean(userStatus) : true;
					return buildGameQuest(quest, userStatus, { statusLoaded });
				});
			});
			return result;
		}

		// If quest definitions aren't ready yet but we have statuses (logged-in case)
		if (questStatuses && Object.keys(questStatuses).length > 0) {
			QUEST_GAME_TYPES.forEach((gameType) => {
				const gameQuests: GameQuest[] = [];

				Object.entries(questStatuses).forEach(([questKey, userStatus]) => {
					if (questKey.startsWith(`${gameType}_`) || (gameType === 'general' && questKey.startsWith('general_'))) {
						const normalizedStatus = userStatus as QuestStatusResult;
						const fallbackQuest: QuestDescription = {
							key: questKey,
							title: formatQuestTitle(questKey),
							description: 'Loading quest details...',
						};
						gameQuests.push(buildGameQuest(fallbackQuest, normalizedStatus, { statusLoaded: true }));
					}
				});

				if (gameQuests.length > 0) {
					result[gameType] = gameQuests;
				}
			});
		}

		return result;
	}, [questsByGame, questStatuses, currentAccount]);

	const totalClaimedXp = useMemo(() => {
		return Object.values(questsWithStatus).reduce((sum, quests) => {
			return (
				sum +
				quests.reduce((questSum, quest) => {
					return questSum + (quest.claimStatus === 'claimed' ? (quest.points ?? 0) : 0);
				}, 0)
			);
		}, 0);
	}, [questsWithStatus]);

	const claimQuestMutation = useMutation({
		mutationFn: async (questKey: string) => {
			if (!currentAccount) throw new Error('No account connected');
			return await questRepository.claimQuest('suigar', questKey, currentAccount.address);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['allQuestStatuses'] });
			if (currentAccount?.address) {
				queryClient.invalidateQueries({ queryKey: ['questClaimHistory', 'suigar', currentAccount.address] });
			}
		},
	});

	const claimQuest = async (questKey: string) => {
		try {
			await claimQuestMutation.mutateAsync(questKey);
			return { success: true };
		} catch (error) {
			return { success: false, error: error instanceof Error ? error.message : 'Failed to claim quest' };
		}
	};

	// Show loading while fetching quest definitions. Also show status loading when logged in.
	const combinedLoading = questsLoading || (!!currentAccount && statusesLoading);

	const refreshQuests = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ['allQuestStatuses', currentAccount?.address, timePeriodFilter] });
		queryClient.invalidateQueries({ queryKey: ['allQuests', timePeriodFilter] });
	}, [queryClient, currentAccount?.address, timePeriodFilter]);

	if (!QUESTS_ENABLED) {
		return ALL_QUESTS_DISABLED;
	}

	return {
		questsByGame: questsWithStatus,
		totalClaimedXp,
		isLoading: combinedLoading,
		error: questsError || statusesError,
		claimQuest,
		refreshQuests,
	};
};

export const useGameQuests = (gameType: string, campaignSlug: string = 'suigar', context: 'game' | 'standalone' = 'standalone') => {
	const { currentAccount } = useAuth();

	const [changedQuests, setChangedQuests] = useState<Set<string>>(new Set());
	const previousProgressRef = useRef<Record<string, number>>({});
	const queryClient = useQueryClient();

	// Fetch all available quests for this game type
	const {
		data: allQuests,
		isLoading: questsLoading,
		error: questsError,
	} = useQuery({
		queryKey: ['quests', gameType, context],
		queryFn: async () => {
			const quests = await questRepository.getAllQuests(gameType);
			// Add gameType to each quest
			return quests.map((quest) => ({ ...quest, gameType }));
		},
		enabled: QUESTS_ENABLED,
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

	// Get the filtered quests for this specific game
	const gameQuests = useMemo(() => {
		if (!allQuests) return [];

		// For game pages, only show game-specific quests (exclude general)
		if (context === 'game') {
			return allQuests.filter((quest) => quest.key.startsWith(`${gameType}_`));
		}

		// For quest page, show both game-specific and general quests
		return allQuests.filter((quest) => quest.key.startsWith(`${gameType}_`) || quest.key.startsWith('general_'));
	}, [allQuests, gameType, context]);

	// Fetch quest statuses for the current user
	const {
		data: questStatuses,
		isLoading: statusesLoading,
		error: statusesError,
	} = useQuery({
		queryKey: ['questStatuses', gameType, currentAccount?.address, campaignSlug],
		queryFn: async () => {
			if (!currentAccount?.address || !gameQuests || gameQuests.length === 0) {
				return {};
			}

			try {
				const payload = gameQuests.map((quest) => ({ key: quest.key, mode: quest.timePeriod }));
				return await questRepository.checkQuestStatuses(campaignSlug, currentAccount.address, payload);
			} catch (_error) {
				return {};
			}
		},
		enabled: QUESTS_ENABLED && !!currentAccount?.address && !!gameQuests && gameQuests.length > 0,
		staleTime: 30 * 1000, // 30 seconds
		refetchInterval: 60 * 1000, // Refetch every minute
		retry: 1, // Only retry once on failure
	});

	// Combine quest data with status to create enriched quests
	const enrichedQuests: GameQuest[] = useMemo(() => {
		if (!gameQuests || gameQuests.length === 0) return [];

		return gameQuests.map((quest) => {
			const status = questStatuses?.[quest.key];
			const statusLoaded = currentAccount ? Boolean(status) : true;
			return buildGameQuest(quest, status, { statusLoaded });
		});
	}, [gameQuests, questStatuses, currentAccount]);

	// Simple effect to detect progress changes
	useEffect(() => {
		if (!enrichedQuests || enrichedQuests.length === 0) return;

		const changedKeys = new Set<string>();

		enrichedQuests.forEach((quest) => {
			const currentProgress = quest.progress;
			const previousProgress = previousProgressRef.current[quest.key];

			// Only check if we have a previous value (skip first load)
			if (previousProgress !== undefined && currentProgress !== previousProgress) {
				changedKeys.add(quest.key);
			}

			// Update stored progress
			previousProgressRef.current[quest.key] = currentProgress;
		});

		if (changedKeys.size > 0) {
			setChangedQuests(changedKeys);
			// Clear highlights after 2 seconds
			const timeoutId = setTimeout(() => setChangedQuests(new Set()), 2000);
			return () => clearTimeout(timeoutId);
		}
	}, [enrichedQuests]);

	const isLoading = questsLoading || (!!gameQuests && gameQuests.length > 0 && statusesLoading);

	// Claim quest mutation
	const claimQuest = useMutation({
		mutationFn: async ({ questKey }: { questKey: string }) => {
			if (!currentAccount?.address) {
				throw new Error('No account connected');
			}
			return questRepository.claimQuest(campaignSlug, questKey, currentAccount.address);
		},
		onSuccess: () => {
			// Refetch quest statuses after claiming
			queryClient.invalidateQueries({
				queryKey: ['questStatuses', gameType, currentAccount?.address, campaignSlug],
			});
			if (currentAccount?.address) {
				queryClient.invalidateQueries({ queryKey: ['questClaimHistory', campaignSlug, currentAccount.address] });
			}
		},
	});

	const refreshQuests = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ['questStatuses', gameType, currentAccount?.address, campaignSlug] });
		queryClient.invalidateQueries({ queryKey: ['quests', gameType, context] });
	}, [queryClient, gameType, currentAccount?.address, campaignSlug, context]);

	if (!QUESTS_ENABLED) {
		return makeGameQuestsDisabled();
	}

	return {
		quests: enrichedQuests,
		isLoading,
		error: questsError || statusesError,
		changedQuests,
		claimQuest,
		refreshQuests,
	};
};
